import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCampaigns } from "../../services/notification";
import {
  getCampaignStatus,
  getCampaignZoneDisplay,
} from "../campaign/hooks/useCampaignListData";
import { isTreatmentRateAvailable } from "../campaign/campaignFormatUtils";
import ProgressBar from "../../ui/components/ProgressBar";
import PillButton from "../../ui/components/PillButton";
import { ReactComponent as PinIcon } from "../../static/assets/dashboard-pin.svg";
import { ReactComponent as CalendarIcon } from "../../static/assets/dashboard-calendar.svg";
import { readBooleanStorage, writeBooleanStorage } from "./dashboardKpiUtils";

const STORAGE_KEY = "dashboard.campaignsOpen";

const formatCampaignDate = (date, locale) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString(locale || "fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const DashboardCampaignCard = ({ campaign, locale, t, onOpen }) => {
  const zoneDisplay = getCampaignZoneDisplay(campaign);
  const zoneLabel = campaign.zoneLabel || zoneDisplay.zoneLabel;
  const treatedCount = campaign.totalTreated ?? 0;
  const percent = campaign.treatmentPercent ?? campaign.progressionPercent;
  const showTreatmentRate =
    isTreatmentRateAvailable(campaign.stop) &&
    percent != null &&
    !Number.isNaN(Number(percent)) &&
    Number(percent) >= 0;

  return (
    <article className="dashboard-campaign-card">
      <div className="dashboard-campaign-header">
        <p className="dashboard-campaign-address">
          {campaign.addressName || "—"}
        </p>
        <PillButton size="sm" onClick={() => onOpen(campaign)}>
          {t("dashboard.view")}
        </PillButton>
      </div>
      {campaign.addressCity ? (
        <div className="dashboard-campaign-city">
          <PinIcon className="dashboard-campaign-pin" aria-hidden="true" />
          <span>{campaign.addressCity}</span>
        </div>
      ) : null}
      {zoneLabel ? (
        <p className="dashboard-campaign-zones">{zoneLabel}</p>
      ) : null}
      <div className="dashboard-campaign-dates">
        <CalendarIcon
          className="dashboard-campaign-calendar"
          aria-hidden="true"
        />
        <span>
          {t("dashboard.campaignDateRange", {
            start: formatCampaignDate(campaign.start, locale),
            end: formatCampaignDate(campaign.stop, locale),
          })}
        </span>
      </div>
      <hr className="dashboard-campaign-divider" />
      <p className="dashboard-campaign-errors">
        <span className="dashboard-campaign-errors-count">{treatedCount}</span>{" "}
        {t(
          `dashboard.sortingErrorsTreatedLabel${treatedCount > 1 ? "_plural" : ""}`,
        )}
      </p>
      {showTreatmentRate ? (
        <ProgressBar
          className="dashboard-campaign-progress"
          value={percent}
          valueClassName="dashboard-campaign-percent"
        />
      ) : null}
    </article>
  );
};

export const DashboardCampaigns = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [campaigns, setCampaigns] = useState([]);
  const [isOpen, setIsOpen] = useState(() =>
    readBooleanStorage(STORAGE_KEY, true),
  );

  const toggleOpen = () => {
    setIsOpen((current) => {
      const next = !current;
      writeBooleanStorage(STORAGE_KEY, next);
      return next;
    });
  };

  useEffect(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const stop = new Date(now);
    stop.setHours(23, 59, 59, 999);

    getCampaigns({
      start: start.toISOString(),
      stop: stop.toISOString(),
    })
      .then((response) => {
        const inProgress = (response.data.campaigns || [])
          .map((campaign) => {
            const zoneDisplay = getCampaignZoneDisplay(campaign);
            return {
              ...campaign,
              status: getCampaignStatus(campaign),
              zoneLabel: zoneDisplay.zoneLabel,
              addressName: campaign.address?.name || "-",
              addressCity: campaign.address?.city?.trim() || "",
            };
          })
          .filter((campaign) => campaign.status === "InProgress");
        setCampaigns(inProgress);
      })
      .catch(() => {
        setCampaigns([]);
      });
  }, []);

  const handleOpen = (campaign) => {
    history.push(`/campaign/${campaign._id}`, { campaign });
  };

  const handleViewAll = () => {
    history.push("/campaign");
  };

  return (
    <section className="dashboard-campaigns">
      <div className={`dashboard-sorting-header${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="dashboard-sorting-toggle"
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-controls="dashboard-campaigns-panel"
        >
          <h3 className="dashboard-campaigns-title">
            {t("Campaign in progress")} ({campaigns.length})
          </h3>
          <i
            className={`fas fa-chevron-down dashboard-sorting-chevron${isOpen ? " is-open" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {isOpen ? (
        <div id="dashboard-campaigns-panel">
          {campaigns.map((campaign) => (
            <DashboardCampaignCard
              key={campaign._id}
              campaign={campaign}
              locale={i18n.language}
              t={t}
              onOpen={handleOpen}
            />
          ))}
          <PillButton
            size="md"
            className="dashboard-campaigns-all"
            onClick={handleViewAll}
          >
            {t("dashboard.viewAllCampaigns")}
          </PillButton>
        </div>
      ) : null}
    </section>
  );
};
